import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { DefaultOptionType } from "antd/es/select";
import { defineMessages, useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { isDefined } from "@mapmycustomers/shared/util/assert";
import { SelectField } from "@mapmycustomers/ui";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import { getEditedLayout } from "@app/scene/settings/component/FormLayouts/store";
import { getActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";

import DefaultCheckInVariant from "../../store/DefaultCheckInVariant";

import styles from "./DefaultCheckInVariantSelect.module.scss";

const messages = defineMessages({
  label: {
    id: "settings.formLayouts.modal.variants.defaultCheckinVariantEdit.select.label",
    defaultMessage: "Check-in default variant",
    description: "Set check-in default variant select label",
  },
  placeholder: {
    id: "settings.formLayouts.modal.variants.defaultCheckinVariantEdit.select.placeholder",
    defaultMessage: "Select default",
    description: "Set check-in default variant select placeholder",
  },
  primaryLayoutOptionLabel: {
    id: "settings.formLayouts.modal.variants.defaultCheckinVariantEdit.select.primaryLayoutOptionLabel",
    defaultMessage: "Primary Layout",
    description: "Set check-in default variant select primary layout option label",
  },
  variantLayoutOptionLabel: {
    id: "settings.formLayouts.modal.variants.defaultCheckinVariantEdit.select.variantLayoutOptionLabel",
    defaultMessage: "{activityTypeName} Variant",
    description: "Set check-in default variant select variant layout option label",
  },
});

const getDefaultCheckInVariantValue = (
  defaultCheckInVariant?: DefaultCheckInVariant
): null | number => {
  if (!defaultCheckInVariant) {
    return null;
  }

  if (defaultCheckInVariant.isPrimary) {
    return -1;
  }

  return defaultCheckInVariant.id;
};

interface DefaultCheckInVariantSelectProps {
  activityTypes: ActivityType[];
  layout: FormLayout;
  onChange(value?: DefaultCheckInVariant): void;
  value?: DefaultCheckInVariant;
}

const DefaultCheckInVariantSelect: React.FC<DefaultCheckInVariantSelectProps> = ({
  activityTypes,
  layout,
  onChange,
  value,
}) => {
  const intl = useIntl();

  const defaultCheckInVariants: DefaultCheckInVariant[] = useMemo(() => {
    return [
      {
        isPrimary: true,
      },
      ...layout
        .childLayouts!.map((childLayout) => {
          const activityType = activityTypes.find(
            (activityType) => activityType.id === childLayout.crmActivityType?.id
          )!;
          return activityType.checkInEnabled
            ? {
                id: childLayout.id,
                isPrimary: false,
              }
            : undefined;
        })
        .filter(isDefined),
    ];
  }, [activityTypes, layout]);

  const defaultCheckInVariantOptions: DefaultOptionType[] = useMemo(
    () =>
      defaultCheckInVariants.map((defaultCheckInVariant) => {
        let activityType: ActivityType | undefined;
        if (!defaultCheckInVariant.isPrimary) {
          const childLayout = layout.childLayouts!.find(
            (childLayout) => childLayout.id === defaultCheckInVariant.id
          )!;
          activityType = activityTypes.find(
            (activityType) => activityType.id === childLayout.crmActivityType?.id
          )!;
        }
        return {
          label: (
            <div className={styles.variantItem}>
              <ActivityTypeIcon activityType={activityType} size="sm" />
              <span className={styles.variantItemText}>
                {activityType
                  ? intl.formatMessage(messages.variantLayoutOptionLabel, {
                      activityTypeName: activityType.name,
                    })
                  : intl.formatMessage(messages.primaryLayoutOptionLabel)}
              </span>
            </div>
          ),
          value: getDefaultCheckInVariantValue(defaultCheckInVariant),
        };
      }),
    [activityTypes, defaultCheckInVariants, intl, layout.childLayouts]
  );

  const selectValue = getDefaultCheckInVariantValue(value);

  const handleChange = useCallback(
    (value?: number) => {
      const defaultCheckInVariant = value
        ? defaultCheckInVariants.find(
            (defaultCheckinVariant) =>
              getDefaultCheckInVariantValue(defaultCheckinVariant) === value
          )
        : undefined;
      onChange(defaultCheckInVariant);
    },
    [defaultCheckInVariants, onChange]
  );

  return (
    <SelectField<number>
      allowClear
      label={intl.formatMessage(messages.label)}
      onChange={handleChange}
      options={defaultCheckInVariantOptions}
      value={selectValue}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  layout: getEditedLayout(state)!,
});

export default connect(mapStateToProps)(DefaultCheckInVariantSelect);
